import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type FeedbackStateTone = 'muted' | 'danger' | 'warning';
export type FeedbackStateSize = 'sm' | 'md' | 'lg';

interface FeedbackStateProps {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  tone?: FeedbackStateTone;
  size?: FeedbackStateSize;
  className?: string;
  headingLevel?: 'h1' | 'h2' | 'h3';
}

const toneClasses: Record<FeedbackStateTone, { circle: string; icon: string }> = {
  muted: {
    circle: 'bg-gray-100 dark:bg-gray-700/70',
    icon: 'text-muted',
  },
  danger: {
    circle: 'bg-rose-50 dark:bg-rose-950/40',
    icon: 'text-rose-500 dark:text-rose-400',
  },
  warning: {
    circle: 'bg-amber-50 dark:bg-amber-950/40',
    icon: 'text-amber-600 dark:text-amber-400',
  },
};

const sizeClasses: Record<FeedbackStateSize, { pad: string; circle: string; icon: string; title: string }> = {
  sm: { pad: 'py-6 px-4', circle: 'h-12 w-12', icon: 'h-6 w-6', title: 'text-base' },
  md: { pad: 'py-12 px-4', circle: 'h-16 w-16', icon: 'h-7 w-7', title: 'text-lg' },
  lg: { pad: 'py-16 px-4', circle: 'h-20 w-20', icon: 'h-8 w-8', title: 'text-xl' },
};

export function FeedbackState({
  icon: Icon,
  title,
  description,
  action,
  tone = 'muted',
  size = 'md',
  className,
  headingLevel: Heading = 'h3',
}: FeedbackStateProps) {
  const toneStyle = toneClasses[tone];
  const sizeStyle = sizeClasses[size];

  return (
    <div className={cn('flex flex-col items-center justify-center text-center', sizeStyle.pad, className)}>
      <div className={cn('mb-4 flex items-center justify-center rounded-full', sizeStyle.circle, toneStyle.circle)}>
        <Icon className={cn(sizeStyle.icon, toneStyle.icon)} aria-hidden />
      </div>
      <Heading className={cn('font-semibold text-foreground', sizeStyle.title)}>{title}</Heading>
      {description ? <div className='mt-2 max-w-md text-sm text-muted-strong'>{description}</div> : null}
      {action ? <div className='mt-5'>{action}</div> : null}
    </div>
  );
}
